/**
 * GenericUSBSigner - Generic USB Token Signer
 *
 * Một class duy nhất cho TẤT CẢ các USB Token providers.
 * Cấu hình qua ProviderEndpointConfig.
 */

import { AbstractUSBSigner } from '../AbstractUSBSigner';
import {
  SignProvider,
  SigningResult,
  SigningOptions,
  XmlSigningOptions,
  BatchSigningItem,
  BatchSigningResult,
  CertificateInfo,
} from '../../types';

/**
 * Cấu hình endpoints cho USB Provider
 */
export interface USBEndpointConfig {
  /** Tên provider */
  provider: SignProvider;
  /** Tên hiển thị */
  displayName: string;
  /** Port của plugin */
  port: number;
  /** Endpoints */
  endpoints: {
    /** Lấy session */
    getSession: string;
    /** Lấy certificate */
    getCertificate: string;
    /** Ký file */
    signFile: string;
    /** Ký XML */
    signXml: string;
    /** Ký batch */
    signBatch?: string;
    /** Health check */
    health?: string;
    /** Get last error */
    getLastError?: string;
  };
  /** Request format */
  requestFormat?: 'form' | 'json';
  /** Session param name */
  sessionParam?: string;
  /** Các lib cần load (cho một số provider như Viettel) */
  libList?: string;
}

/**
 * Generic USB Signer - Cấu hình qua config, không cần tạo class riêng
 */
export class GenericUSBSigner extends AbstractUSBSigner {
  readonly signProvider: SignProvider;
  readonly displayName: string;

  private endpointConfig: USBEndpointConfig;
  private processing = false;

  constructor(config: USBEndpointConfig) {
    super({
      pluginUrl: 'http://127.0.0.1',
      port: config.port,
    });
    this.signProvider = config.provider;
    this.displayName = config.displayName;
    this.endpointConfig = config;
  }

  async initialize(): Promise<boolean> {
    if (this._isReady) return true;

    try {
      const endpoint = this.endpointConfig.endpoints.getSession;
      const sessionParam = this.endpointConfig.sessionParam || 'sessionID';

      let body: string | undefined;
      if (this.endpointConfig.libList) {
        body = `liblist=${this.endpointConfig.libList}`;
      }

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body,
      });

      if (response.ok) {
        const data = await response.text();
        // Có thể là JSON hoặc plain text tùy provider
        try {
          const json = JSON.parse(data);
          this.sessionId = json[sessionParam] || json.sessionId || json.session || data;
        } catch {
          this.sessionId = data;
        }

        if (this.sessionId) {
          this._isReady = true;
          console.log(`${this.displayName} initialized, session:`, this.sessionId);
          return true;
        }
      }

      this.showPluginNotRunningError();
      return false;
    } catch (error) {
      console.error(`${this.displayName} initialization failed:`, error);
      return false;
    }
  }

  async getCertificateInfo(): Promise<CertificateInfo | null> {
    if (!this._isReady) await this.initialize();

    try {
      const endpoint = this.endpointConfig.endpoints.getCertificate;
      const response = await this.makeRequest(endpoint, {});

      if (response.ok) {
        const data = await response.text();
        return {
          serialNumber: 'N/A',
          subject: data.substring(0, 100),
          issuer: this.displayName,
          validFrom: new Date(),
          validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
          isValid: true,
        };
      }
    } catch {}
    return null;
  }

  async signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult> {
    return this.signFile(pdfData, 'document.pdf', options);
  }

  async signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult> {
    if (!this._isReady) {
      const ok = await this.initialize();
      if (!ok) return this.createErrorResult(`${this.displayName} chưa sẵn sàng`);
    }

    if (this.processing) {
      return this.createErrorResult('Đang xử lý, vui lòng chờ');
    }

    this.processing = true;

    try {
      const endpoint = this.endpointConfig.endpoints.signFile;
      const response = await this.makeRequest(endpoint, {
        fileData,
        fileName,
        reason: options?.reason,
      });

      if (response.ok) {
        const data = await this.parseResponse(response);
        if (data.signedFile || data.signedData || typeof data === 'string') {
          return this.createSuccessResult(data.signedFile || data.signedData || data, {
            signerName: this.displayName,
            signedAt: new Date(),
          });
        }
        if (data.error) {
          return this.createErrorResult(data.error);
        }
      }

      // Get last error if available
      if (this.endpointConfig.endpoints.getLastError) {
        const errResponse = await fetch(`${this.baseUrl}${this.endpointConfig.endpoints.getLastError}`, {
          method: 'POST',
        });
        const errCode = await errResponse.text();
        return this.createErrorResult(`Lỗi: ${errCode}`);
      }

      return this.createErrorResult('Ký file thất bại');
    } catch (error: any) {
      return this.createErrorResult(error.message);
    } finally {
      this.processing = false;
    }
  }

  async signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult> {
    if (!this._isReady) {
      const ok = await this.initialize();
      if (!ok) return this.createErrorResult(`${this.displayName} chưa sẵn sàng`);
    }

    try {
      const endpoint = this.endpointConfig.endpoints.signXml;
      const response = await this.makeRequest(endpoint, {
        xmlContent,
        signatureType: options?.signatureType || 'enveloped',
      });

      if (response.ok) {
        const data = await this.parseResponse(response);
        if (data.signedXml || typeof data === 'string') {
          return this.createSuccessResult(data.signedXml || data);
        }
        if (data.error) {
          return this.createErrorResult(data.error);
        }
      }

      return this.createErrorResult('Ký XML thất bại');
    } catch (error: any) {
      return this.createErrorResult(error.message);
    }
  }

  async signBatch(items: BatchSigningItem[], options?: SigningOptions): Promise<BatchSigningResult> {
    // Nếu có endpoint batch riêng
    if (this.endpointConfig.endpoints.signBatch) {
      try {
        const response = await this.makeRequest(this.endpointConfig.endpoints.signBatch, {
          files: items,
          reason: options?.reason,
        });

        if (response.ok) {
          const data = await response.json();
          return {
            success: data.success,
            results: data.results || data.signedXmlList,
          };
        }
      } catch {}
    }

    // Fallback: ký từng file
    return super.signBatch(items, options);
  }

  /**
   * Helper: Tạo request theo format của provider
   */
  private async makeRequest(endpoint: string, params: Record<string, any>): Promise<Response> {
    const url = `${this.baseUrl}${endpoint}`;

    if (this.endpointConfig.requestFormat === 'json') {
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': this.sessionId,
        },
        body: JSON.stringify(params),
      });
    }

    // Default: form-urlencoded
    const formData = new URLSearchParams();
    formData.append(this.endpointConfig.sessionParam || 'sessionID', this.sessionId);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        formData.append(key, String(value));
      }
    });

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
    });
  }

  /**
   * Helper: Parse response (JSON hoặc text)
   */
  private async parseResponse(response: Response): Promise<any> {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  dispose(): void {
    this.processing = false;
    super.dispose();
  }
}
